import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import * as yup from 'yup';

dayjs.locale('pt-br');

import { VTextField, VForm, useVForm, IVFormErrors, VSwitch, VTextFieldCpfCnpj, cpfcnpjValidator, BasicSelectUF, MoneyTextField } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { EmpresaAdminService, IEmpresaFormData, IUsuarioDaEmpresa } from '../../../shared/services/api/controle/EmpresaAdminService';
import { MensalidadeService, IMensalidade } from '../../../shared/services/api/controle/MensalidadeService';
import { IComprovanteMensalidadeDados } from '../../mensalidades/ComprovanteMensalidade';
import { DialogVisualizarComprovante } from '../../mensalidades/DialogVisualizarComprovante';
import { Environment } from '../../../shared/environment';
import { proximoDiaUtil } from '../../../shared/utils/feriadosBrasil';

interface IFormData {
  nome: string;
  fantasia: string;
  cnpj: string;
  telefone: string;
  whatsapp: string;
  endereco: string;
  numero: string;
  bairro: string;
  uf: string;
  cep: string;
  banco: string;
  status: boolean;
  sistematravado: boolean;
  modulovendas: boolean;
  modulocadastros: boolean;
  moduloestoque: boolean;
  modulofinanceiro: boolean;
  modulofiscal: boolean;
  moduloservicos: boolean;
  comissaoativa: boolean;
}

type TCampoModulo = 'modulovendas' | 'modulocadastros' | 'moduloestoque' | 'modulofinanceiro' | 'modulofiscal' | 'moduloservicos';

// Módulos do ERP que podem ser liberados por empresa — desligado, o módulo
// some do menu lateral do SIAC para todos os usuários da empresa.
const MODULOS: { campo: TCampoModulo; label: string; descricao: string }[] = [
  { campo: 'modulovendas', label: 'Vendas', descricao: 'PDV, vendas e gráficos de vendas' },
  { campo: 'modulocadastros', label: 'Cadastros', descricao: 'Parceiros, transportadoras, usuários e plano de contas' },
  { campo: 'moduloestoque', label: 'Estoque', descricao: 'Produtos, entradas, grupos e relatórios de estoque' },
  { campo: 'modulofinanceiro', label: 'Financeiro', descricao: 'Contas a pagar/receber, conciliação e faturamento' },
  { campo: 'modulofiscal', label: 'Fiscal', descricao: 'NF-e, NFC-e, notas de entrada e relatórios fiscais' },
  { campo: 'moduloservicos', label: 'Serviços', descricao: 'Ordens de serviço e bens/patrimônio' },
];

// Empresa nova começa com tudo liberado.
const TODOS_MODULOS_LIGADOS = { ...Object.fromEntries(MODULOS.map(m => [m.campo, true])), comissaoativa: true } as Record<TCampoModulo, boolean> & { comissaoativa: boolean };

// Plano "Básico": só o necessário pra vender pelo PDV, emitir NF-e/NF-e de entrada e
// controlar estoque (via nota e manual) — Financeiro e Serviços ficam de fora. Cadastros
// continua ligado porque Vendas/Fiscal dependem dele (parceiros, operações). Comissão não
// entra no preset — é uma escolha à parte (ver botão "Sem comissionamento" abaixo).
const PLANO_BASICO: Record<TCampoModulo, boolean> = {
  modulovendas: true,
  modulocadastros: true,
  moduloestoque: true,
  modulofiscal: true,
  modulofinanceiro: false,
  moduloservicos: false,
};

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  nome: yup.string().required(),
  fantasia: yup.string().required(),
  cnpj: yup.string().required().test('cnpj', 'CNPJ inválido', cpfcnpjValidator),
  telefone: yup.string().default(''),
  whatsapp: yup.string().default(''),
  endereco: yup.string().default(''),
  numero: yup.string().default(''),
  bairro: yup.string().default(''),
  uf: yup.string().default(''),
  cep: yup.string().default(''),
  banco: yup.string().default(''),
  status: yup.boolean().required().default(true),
  sistematravado: yup.boolean().required().default(false),
  modulovendas: yup.boolean().required().default(true),
  modulocadastros: yup.boolean().required().default(true),
  moduloestoque: yup.boolean().required().default(true),
  modulofinanceiro: yup.boolean().required().default(true),
  modulofiscal: yup.boolean().required().default(true),
  moduloservicos: yup.boolean().required().default(true),
  comissaoativa: yup.boolean().required().default(true),
});

const statusMensalidadeCor: Record<string, { bg: string; fg: string; label: string }> = {
  pago: { bg: 'success.main', fg: '#fff', label: 'Pago' },
  pendente: { bg: 'warning.main', fg: '#fff', label: 'Pendente' },
  atrasado: { bg: 'error.main', fg: '#fff', label: 'Atrasado' },
  cancelado: { bg: 'text.disabled', fg: '#fff', label: 'Cancelado' },
};

const situacaoMensalidade = (m: IMensalidade): string => {
  if (m.status === 'pendente' && dayjs(m.vencimento).isBefore(dayjs(), 'day')) return 'atrasado';
  return m.status;
};

const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export const DetalheDeEmpresa: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();

  const [nome, setNome] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [usuarios, setUsuarios] = useState<IUsuarioDaEmpresa[]>([]);
  const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(false);

  const [mensalidades, setMensalidades] = useState<IMensalidade[]>([]);
  const [isLoadingMensalidades, setIsLoadingMensalidades] = useState(false);

  const [openGerar, setOpenGerar] = useState(false);
  const [anoGerar, setAnoGerar] = useState(String(new Date().getFullYear()));
  const [mesInicialGerar, setMesInicialGerar] = useState(String(new Date().getMonth() + 1));
  const [quantidadeMesesGerar, setQuantidadeMesesGerar] = useState('12');
  const [valorGerar, setValorGerar] = useState(0);
  const [impostoGerar, setImpostoGerar] = useState(0);
  const [diaVencimentoGerar, setDiaVencimentoGerar] = useState('10');
  const [isGerando, setIsGerando] = useState(false);

  const [dialogBaixa, setDialogBaixa] = useState<IMensalidade | null>(null);
  const [dataPagamento, setDataPagamento] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [formaPagamento, setFormaPagamento] = useState('Pix');
  const [isBaixando, setIsBaixando] = useState(false);

  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteMensalidadeDados | null>(null);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const carregarUsuarios = (idempresa: string) => {
    setIsLoadingUsuarios(true);
    EmpresaAdminService.getUsuarios(idempresa).then((result) => {
      setIsLoadingUsuarios(false);
      if (!(result instanceof Error)) setUsuarios(result.data);
    });
  };

  const carregarMensalidades = (idempresa: string) => {
    setIsLoadingMensalidades(true);
    MensalidadeService.getAll(1, 24, { idempresa }).then((result) => {
      setIsLoadingMensalidades(false);
      if (!(result instanceof Error)) setMensalidades(result.data);
    });
  };

  useEffect(() => {
    if (id === 'novo') {
      formRef.current?.setData({ status: true, sistematravado: false, ...TODOS_MODULOS_LIGADOS });
      return;
    }
    setIsLoading(true);
    EmpresaAdminService.getById(id).then((result) => {
      setIsLoading(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setNome(result.nome);
        // Coluna nula/ausente conta como ligada (mesmo critério do ERP) — o
        // switch nunca recebe undefined. `sistematravado` é o oposto: nula/ausente
        // conta como destravado (comportamento padrão, sem bloqueio).
        const modulos = Object.fromEntries(MODULOS.map(m => [m.campo, result[m.campo] !== false]));
        formRef.current?.setData({
          ...result,
          ...modulos,
          comissaoativa: result.comissaoativa !== false,
          sistematravado: result.sistematravado === true,
        });
      }
    });
    carregarUsuarios(id);
    carregarMensalidades(id);
  }, [id]);

  // Atalhos que só preenchem os switches de módulo no formulário — não salvam sozinhos,
  // o usuário ainda ajusta manualmente e confirma no botão Salvar de sempre.
  const handleAplicarPlanoBasico = () => formRef.current?.setData(PLANO_BASICO);
  const handleLiberarTudoModulos = () => formRef.current?.setData(TODOS_MODULOS_LIGADOS);

  const handleSave = (dadosOriginais: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema
      .validate(dadosOriginais, { abortEarly: false })
      .then(async (dadosValidados) => {
        const dados = dadosValidados as IEmpresaFormData;
        const result = id === 'novo'
          ? await EmpresaAdminService.create(dados)
          : await EmpresaAdminService.updateById(id, dados);

        if (result instanceof Error) {
          cancelarSalvamento();
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
          return;
        }

        concluirSalvamento();
        setTms('success');
        setMsg('Empresa salva com sucesso!');
        setOpenmsg(true);

        if (id === 'novo' && result && 'idempresa' in result) {
          navigate(`/empresas/${result.idempresa}`, { replace: true });
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleAbrirGerar = () => {
    setValorGerar(0);
    setImpostoGerar(0);
    setAnoGerar(String(new Date().getFullYear()));
    setMesInicialGerar(String(new Date().getMonth() + 1));
    setQuantidadeMesesGerar('12');
    setDiaVencimentoGerar('10');
    setOpenGerar(true);
  };

  const handleGerarMensalidades = () => {
    if (!valorGerar || valorGerar <= 0) {
      setTms('error');
      setMsg('Informe um valor de mensalidade válido.');
      setOpenmsg(true);
      return;
    }
    if (impostoGerar >= valorGerar) {
      setTms('error');
      setMsg('O imposto não pode ser maior ou igual ao valor da mensalidade.');
      setOpenmsg(true);
      return;
    }
    if (!Number(quantidadeMesesGerar) || Number(quantidadeMesesGerar) <= 0) {
      setTms('error');
      setMsg('Informe uma quantidade de meses válida.');
      setOpenmsg(true);
      return;
    }
    setIsGerando(true);
    MensalidadeService.gerarAno(id, Number(anoGerar), Number(mesInicialGerar), Number(quantidadeMesesGerar), valorGerar, impostoGerar, Number(diaVencimentoGerar))
      .then((result) => {
        setIsGerando(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
          return;
        }
        setOpenGerar(false);
        setTms('success');
        setMsg(`${quantidadeMesesGerar} mensalidade${Number(quantidadeMesesGerar) !== 1 ? 's' : ''} gerada${Number(quantidadeMesesGerar) !== 1 ? 's' : ''} com sucesso!`);
        setOpenmsg(true);
        carregarMensalidades(id);
      });
  };

  // Prévia das parcelas que serão geradas, recalculada a cada mudança nos campos do
  // diálogo — mesma regra de rolagem de mês/ano, vencimento pós-pago (mês seguinte ao
  // de competência) e ajuste pro próximo dia útil (fim de semana/feriado nacional) do
  // backend (GerarAno.ts).
  const parcelasPreview = useMemo(() => {
    const ano = Number(anoGerar);
    const mesInicial = Number(mesInicialGerar);
    const quantidade = Number(quantidadeMesesGerar);
    const diaVencimento = Number(diaVencimentoGerar);
    if (!ano || !mesInicial || !quantidade || quantidade <= 0 || !diaVencimento) return [];

    return Array.from({ length: Math.min(quantidade, 60) }, (_, i) => {
      const indiceMes = mesInicial - 1 + i;
      const anoVencimento = ano + Math.floor((indiceMes + 1) / 12);
      const mesVencimento = ((indiceMes + 1) % 12) + 1;
      const ultimoDiaVencimento = new Date(anoVencimento, mesVencimento, 0).getDate();
      const dia = Math.min(diaVencimento, ultimoDiaVencimento);
      const dataVencimento = proximoDiaUtil(new Date(anoVencimento, mesVencimento - 1, dia));

      return {
        ano: ano + Math.floor(indiceMes / 12),
        mes: (indiceMes % 12) + 1,
        vencimento: dayjs(dataVencimento),
      };
    });
  }, [anoGerar, mesInicialGerar, quantidadeMesesGerar, diaVencimentoGerar]);

  const valorLiquidoGerar = Math.max(0, valorGerar - impostoGerar);
  const totalPreview = parcelasPreview.length * valorLiquidoGerar;

  const handleAbrirBaixa = (mensalidade: IMensalidade) => {
    setDataPagamento(dayjs().format('YYYY-MM-DD'));
    setFormaPagamento('Pix');
    setDialogBaixa(mensalidade);
  };

  const handleConfirmarBaixa = () => {
    if (!dialogBaixa) return;
    setIsBaixando(true);
    MensalidadeService.baixa(dialogBaixa.idmensalidade, dataPagamento, formaPagamento).then((result) => {
      setIsBaixando(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setComprovanteDados({
        idmensalidade: dialogBaixa.idmensalidade,
        nomeempresa: dialogBaixa.nomeempresa,
        fantasiaempresa: dialogBaixa.fantasiaempresa,
        mes: dialogBaixa.mes,
        ano: dialogBaixa.ano,
        valor: dialogBaixa.valor,
        datapagamento: dataPagamento,
        formapagamento: formaPagamento,
      });
      setDialogBaixa(null);
      carregarMensalidades(id);
    });
  };

  const handleApagarMensalidade = (mensalidade: IMensalidade) => {
    if (!window.confirm(`Apagar a mensalidade de ${dayjs().month(mensalidade.mes - 1).format('MMMM')}/${mensalidade.ano}?`)) return;
    MensalidadeService.deleteById(mensalidade.idmensalidade).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      carregarMensalidades(id);
    });
  };

  const handleReimprimirComprovante = (mensalidade: IMensalidade) => {
    setComprovanteDados({
      idmensalidade: mensalidade.idmensalidade,
      nomeempresa: mensalidade.nomeempresa,
      fantasiaempresa: mensalidade.fantasiaempresa,
      mes: mensalidade.mes,
      ano: mensalidade.ano,
      valor: mensalidade.valor,
      datapagamento: mensalidade.datapagamento || '',
      formapagamento: mensalidade.formapagamento || '',
    });
  };

  const mesLabel = (mes: number) => dayjs().month(mes - 1).format('MMMM');

  return (
    <>
      <TelaBloqueioSalvando open={isSaving} message="Salvando dados da empresa..." progress={saveProgress} />

      <LayoutBaseDePagina
        titulo={id === 'novo' ? 'Nova Empresa' : nome || 'Empresa'}
        subtitulo='Cadastro da empresa cliente do SIAC.'
        barraDeFerramentas={
          <FerramentasDeDetalhe
            mostrarBotaoNovo={id !== 'novo'}
            mostrarBotaoApagar={false}
            mostrarBotaoImpressao={false}
            disableBotaoSalvar={isLoading || isSaving}
            aoClicarEmVoltar={() => navigate('/empresas')}
            aoClicarEmSalvar={save}
            aoClicarEmNovo={() => navigate('/empresas/novo')}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openmsg}
          autoHideDuration={tms === 'error' ? null : 6000}
          onClose={handleCloseMsg}
        >
          <Alert onClose={handleCloseMsg} severity={tms} variant='filled' sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <VForm ref={formRef} onSubmit={handleSave}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <VTextField fullWidth name='nome' label='Razão social' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={4}>
                <VTextField fullWidth name='fantasia' label='Nome fantasia' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={3}>
                <VTextFieldCpfCnpj fullWidth name='cnpj' label='CNPJ' disabled={isLoading} />
              </Grid>

              <Grid item xs={12} md={3}>
                <VTextField fullWidth name='telefone' label='Telefone' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={3}>
                <VTextField
                  fullWidth
                  name='whatsapp'
                  label='WhatsApp'
                  disabled={isLoading}
                  helperText='Número que recebe os avisos de mensalidade em atraso'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <VTextField fullWidth name='endereco' label='Endereço' disabled={isLoading} />
              </Grid>
              <Grid item xs={6} md={2}>
                <VTextField fullWidth name='numero' label='Número' disabled={isLoading} />
              </Grid>

              <Grid item xs={6} md={2}>
                <BasicSelectUF name='uf' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={4}>
                <VTextField fullWidth name='bairro' label='Bairro' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={3}>
                <VTextField fullWidth name='cep' label='CEP' disabled={isLoading} />
              </Grid>
              <Grid item xs={12} md={3}>
                <VTextField
                  fullWidth
                  name='banco'
                  label='Banco de dados (tenant)'
                  disabled={isLoading}
                  helperText='Nome técnico do banco de dados do ERP dessa empresa'
                />
              </Grid>
              <Grid item xs={12} md={2} display='flex' flexDirection='column' justifyContent='center'>
                <VSwitch name='status' label='Empresa ativa' fdisabled={isLoading} aliamento='end' />
                <Typography variant='caption' color='text.secondary'>
                  Só cadastral/listagem — não bloqueia o acesso ao SIAC
                </Typography>
              </Grid>
              <Grid item xs={12} md={3} display='flex' flexDirection='column' justifyContent='center'>
                <VSwitch name='sistematravado' label='Travar sistema' fdisabled={isLoading} aliamento='end' />
                <Typography variant='caption' color='text.secondary'>
                  Único switch que bloqueia o acesso de todos os usuários dessa empresa ao SIAC
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
              <Typography variant='h6' fontWeight={700}>Módulos contratados</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size='small' variant='outlined' disabled={isLoading} onClick={handleAplicarPlanoBasico}>
                  Plano Básico
                </Button>
                <Button size='small' variant='text' disabled={isLoading} onClick={handleLiberarTudoModulos}>
                  Liberar tudo
                </Button>
              </Box>
            </Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Os módulos desligados somem do menu do SIAC para todos os usuários dessa empresa. “Plano Básico” libera só o
              necessário pra vender pelo PDV, emitir NF-e/NF-e de entrada e controlar estoque — os switches continuam
              ajustáveis manualmente depois de aplicar.
            </Typography>
            <Grid container spacing={2}>
              {MODULOS.map((modulo) => (
                <Grid item xs={12} sm={6} md={4} key={modulo.campo}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', px: 2, py: 1.25, height: '100%' }}>
                    <VSwitch name={modulo.campo} label={modulo.label} fdisabled={isLoading} aliamento='end' />
                    <Typography variant='caption' color='text.secondary' display='block'>
                      {modulo.descricao}
                    </Typography>
                    {modulo.campo === 'modulovendas' && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <VSwitch name='comissaoativa' label='Comissão' fdisabled={isLoading} aliamento='end' />
                        <Typography variant='caption' color='text.secondary' display='block'>
                          Desligada, somem as telas “Minhas Vendas” e “Vendas da Equipe” (PDV e gráfico de vendas continuam)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </VForm>

        {id !== 'novo' && (
          <>
            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', mb: 3 }}>
              <Typography variant='h6' fontWeight={700} gutterBottom>Usuários da empresa</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell>Nome</TableCell>
                      <TableCell>Login</TableCell>
                      <TableCell align='center'>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usuarios.map((usuario) => (
                      <TableRow key={usuario.iduser}>
                        <TableCell>
                          <Avatar sx={{ width: 28, height: 28 }} alt={usuario.nome} src={Environment.URL_IMGUSUARIOS + usuario.urlimg} />
                        </TableCell>
                        <TableCell>{usuario.nome}</TableCell>
                        <TableCell>{usuario.login}</TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={usuario.status ? 'Ativo' : 'Inativo'}
                            size='small'
                            color={usuario.status ? 'success' : 'error'}
                            variant='outlined'
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!isLoadingUsuarios && usuarios.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align='center'>
                          <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 2 }}>
                            Nenhum usuário cadastrado para essa empresa.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', mb: 3 }}>
              <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
                <Typography variant='h6' fontWeight={700}>Mensalidade</Typography>
                <Button variant='outlined' size='small' onClick={handleAbrirGerar}>
                  Gerar mensalidades do ano
                </Button>
              </Box>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Competência</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell align='right'>Valor</TableCell>
                      <TableCell align='center'>Status</TableCell>
                      <TableCell align='center'>Pagamento</TableCell>
                      <TableCell align='right'>Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mensalidades.map((m) => {
                      const situacao = situacaoMensalidade(m);
                      const cor = statusMensalidadeCor[situacao];
                      return (
                        <TableRow key={m.idmensalidade}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{mesLabel(m.mes)}/{m.ano}</TableCell>
                          <TableCell>{dayjs(m.vencimento).format('DD/MM/YYYY')}</TableCell>
                          <TableCell align='right'>{m.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                          <TableCell align='center'>
                            <Chip label={cor.label} size='small' sx={{ bgcolor: cor.bg, color: cor.fg, fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align='center'>{m.datapagamento ? dayjs(m.datapagamento).format('DD/MM/YYYY') : '-'}</TableCell>
                          <TableCell align='right'>
                            {m.status === 'pendente' && (
                              <Button size='small' onClick={() => handleAbrirBaixa(m)}>Dar baixa</Button>
                            )}
                            {m.status === 'pago' && (
                              <IconButton size='small' onClick={() => handleReimprimirComprovante(m)} title='Reimprimir comprovante'>
                                <PrintRoundedIcon fontSize='small' />
                              </IconButton>
                            )}
                            <IconButton size='small' onClick={() => handleApagarMensalidade(m)} title='Apagar mensalidade'>
                              <DeleteOutlineRoundedIcon fontSize='small' />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!isLoadingMensalidades && mensalidades.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align='center'>
                          <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 2 }}>
                            Nenhuma mensalidade gerada ainda para essa empresa.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </LayoutBaseDePagina>

      <Dialog open={openGerar} onClose={() => setOpenGerar(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Gerar mensalidades do ano</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label='Ano' value={anoGerar} onChange={(e) => setAnoGerar(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label='Dia de vencimento' value={diaVencimentoGerar} onChange={(e) => setDiaVencimentoGerar(e.target.value)} />
            </Grid>
            <Grid item xs={7}>
              <TextField
                fullWidth
                select
                label='Mês da primeira mensalidade'
                value={mesInicialGerar}
                onChange={(e) => setMesInicialGerar(e.target.value)}
              >
                {MESES_PT.map((mes, index) => (
                  <MenuItem key={mes} value={String(index + 1)}>{mes}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                type='number'
                label='Quantidade de meses'
                value={quantidadeMesesGerar}
                onChange={(e) => setQuantidadeMesesGerar(e.target.value)}
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={6}>
              <MoneyTextField fullWidth label='Valor da mensalidade' value={valorGerar} onChange={setValorGerar} />
            </Grid>
            <Grid item xs={6}>
              <MoneyTextField
                fullWidth
                label='Imposto (por mês)'
                value={impostoGerar}
                onChange={setImpostoGerar}
                helperText='Diminui do valor cobrado em cada parcela'
              />
            </Grid>

            {parcelasPreview.length > 0 && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' fontWeight={700} gutterBottom>
                  Prévia das mensalidades
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                  Cobrança pós-paga: o vencimento de cada parcela cai no mês seguinte ao mês de referência, e é empurrado pro próximo dia útil quando cai em fim de semana ou feriado nacional.
                </Typography>
                <TableContainer sx={{ maxHeight: 260, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px' }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Referente a</TableCell>
                        <TableCell>Vencimento</TableCell>
                        {impostoGerar > 0 && <TableCell align='right'>Imposto</TableCell>}
                        <TableCell align='right'>{impostoGerar > 0 ? 'Valor líquido' : 'Valor'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parcelasPreview.map((p) => (
                        <TableRow key={`${p.ano}-${p.mes}`}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{MESES_PT[p.mes - 1]}/{p.ano}</TableCell>
                          <TableCell>{p.vencimento.format('DD/MM/YYYY')}</TableCell>
                          {impostoGerar > 0 && (
                            <TableCell align='right'>{impostoGerar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                          )}
                          <TableCell align='right'>{valorLiquidoGerar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display='flex' justifyContent='space-between' sx={{ mt: 1, px: 0.5 }}>
                  <Typography fontWeight={700}>Total ({parcelasPreview.length}x)</Typography>
                  <Typography fontWeight={700}>{totalPreview.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGerar(false)}>Cancelar</Button>
          <Button variant='contained' disabled={isGerando} onClick={handleGerarMensalidades}>
            Gerar {quantidadeMesesGerar || ''} {Number(quantidadeMesesGerar) === 1 ? 'mês' : 'meses'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!dialogBaixa} onClose={() => setDialogBaixa(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Dar baixa na mensalidade</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type='date'
                label='Data do pagamento'
                InputLabelProps={{ shrink: true }}
                value={dataPagamento}
                onChange={(e) => setDataPagamento(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label='Forma de pagamento' value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)}>
                <MenuItem value='Pix'>Pix</MenuItem>
                <MenuItem value='Boleto'>Boleto</MenuItem>
                <MenuItem value='Cartão'>Cartão</MenuItem>
                <MenuItem value='Transferência'>Transferência</MenuItem>
                <MenuItem value='Dinheiro'>Dinheiro</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogBaixa(null)}>Cancelar</Button>
          <Button variant='contained' disabled={isBaixando} onClick={handleConfirmarBaixa}>Confirmar pagamento</Button>
        </DialogActions>
      </Dialog>

      <DialogVisualizarComprovante
        open={!!comprovanteDados}
        dados={comprovanteDados}
        onClose={() => setComprovanteDados(null)}
      />
    </>
  );
};
